// Game balance configuration - tweak these numbers to adjust difficulty/pacing

// Hardware definitions
export interface HardwareDef {
  id: string
  name?: string
  nameKey?: string
  computePerSec: number
  energyPerSec: number
}

export const HARDWARE: HardwareDef[] = [
  // Tier 1: Garage
  { id: 'laptop', nameKey: 'hwLaptopName', computePerSec: 1, energyPerSec: 1 },
  { id: 'used-gpu', nameKey: 'hwUsedGpuName', computePerSec: 3, energyPerSec: 2 },
  { id: 'gaming-gpu', nameKey: 'hwGamingGpuName', computePerSec: 8, energyPerSec: 5 },
  { id: 'pro-gpu', nameKey: 'hwProGpuName', computePerSec: 20, energyPerSec: 12 },

  // Tier 2: Startup
  { id: 'workstation', nameKey: 'hwWorkstationName', computePerSec: 50, energyPerSec: 30 },
  { id: 'server-rack', nameKey: 'hwServerRackName', computePerSec: 120, energyPerSec: 70 },
  { id: 'gpu-cluster', nameKey: 'hwGpuClusterName', computePerSec: 300, energyPerSec: 150 },

  // Tier 3: Scale-up
  { id: 'mini-datacenter', nameKey: 'hwMiniDatacenterName', computePerSec: 800, energyPerSec: 400 },
  { id: 'datacenter', nameKey: 'hwDatacenterName', computePerSec: 2000, energyPerSec: 1000 },
  { id: 'mega-datacenter', nameKey: 'hwMegaDatacenterName', computePerSec: 5000, energyPerSec: 2500 },

  // Tier 4: Global Infrastructure
  { id: 'datacenter-network', nameKey: 'hwDatacenterNetworkName', computePerSec: 15000, energyPerSec: 7000 },
  { id: 'supercomputer', nameKey: 'hwSupercomputerName', computePerSec: 50000, energyPerSec: 20000 },

  // Tier 5: Next-Gen
  { id: 'satellite', nameKey: 'hwSatelliteName', computePerSec: 200000, energyPerSec: 80000 },
  { id: 'quantum', nameKey: 'hwQuantumName', computePerSec: 1000000, energyPerSec: 300000 },

  // Tier 6: Post-Singularity
  { id: 'dyson-compute', nameKey: 'hwDysonComputeName', computePerSec: 10000000, energyPerSec: 1000000 },
  { id: 'matrioshka', nameKey: 'hwMatrioshkaName', computePerSec: 100000000, energyPerSec: 5000000 },
]

export const BALANCE = {
  // === STARTING VALUES ===
  START_MONEY: 10,
  START_MODEL_QUALITY: 0,
  START_USERS: 0,
  START_PRICE: 5.00, // Starting price per 1M tokens (matches Tiny LM market price)

  // === ENERGY SYSTEM ===
  START_ENERGY: 50,
  START_MAX_ENERGY: 100,
  START_ENERGY_REGEN: 2, // per second

  // === COMPUTE SYSTEM ===
  START_COMPUTE: 20,
  START_MAX_COMPUTE: 50,
  START_HW_POWER: 1, // compute generated per second (consumes equal energy)

  // === ACTION COSTS (in compute) ===
  TRAIN_COMPUTE_COST: 10,
  INFERENCE_COMPUTE_COST: 1, // per inference click
  RESEARCH_COMPUTE_COST: 5,
  RESEARCH_UNLOCK_THRESHOLD: 50, // total compute spent to unlock research

  // === ACTION COSTS (in energy) ===
  TRAIN_ENERGY_COST: 5,
  INFERENCE_ENERGY_COST: 0.5,
  RESEARCH_ENERGY_COST: 3,

  // === Price settings (in $/1M tokens) ===
  MIN_PRICE: 0.00001, // Allow very low prices for ASI era
  MAX_PRICE: 50.00,

  // === Quality & Users ===
  MIN_QUALITY_FOR_USERS: 40, // below this, users start leaving
  CRITICAL_QUALITY: 20, // below this, revenue halved
  BASE_QUALITY_PER_TRAIN: 5, // base quality gained per train action

  // User dynamics
  BASE_USER_ARRIVAL_RATE: 0.5, // users per second at 100% quality
  USER_LEAVE_RATE: 0.02, // users leaving per second when quality low

  // === Upgrade cost scaling ===
  UPGRADE_COST_MULTIPLIER: 1.15, // each same-type upgrade costs 15% more

  // === Time ===
  TICK_RATE: 100, // ms between game ticks (10 ticks/second)
  AUTO_SAVE_INTERVAL: 30000, // ms between auto-saves
} as const

// Model definitions
export interface ModelDef {
  id: string
  name: string
  params: string
  maxUsers: number
  tokensPerTask: number // average tokens consumed per inference/task
  marketPricePerMillion: number // market price per 1M tokens (competitors)
  researchCost: number // research points needed to unlock
  trainComputeCost: number // compute cost per training click
  maxQuality: number // maximum quality achievable with this model
  userGrowthMultiplier: number // multiplier for user arrival rate (scales with model tier)
  description?: string
  descriptionKey?: string // i18n key for translated description
  discontinuesOlderModels?: boolean // if true, training this model discontinues all older models
  triggersVictory?: boolean // if true, training this model triggers the victory screen
}

export const MODELS: ModelDef[] = [
  // Era 1: The Beginning (2017-2018)
  {
    id: 'attention',
    name: 'Transformer',
    params: '10M',
    maxUsers: 100,
    tokensPerTask: 200,
    marketPricePerMillion: 5.00,
    researchCost: 0,
    trainComputeCost: 10,
    maxQuality: 75,
    userGrowthMultiplier: 5,
    descriptionKey: 'modelTransformerDesc',
  },
  {
    id: 'gpt1',
    name: 'GPT-1',
    params: '117M',
    maxUsers: 500,
    tokensPerTask: 400,
    marketPricePerMillion: 4.00,
    researchCost: 50,
    trainComputeCost: 15,
    maxQuality: 78,
    userGrowthMultiplier: 20,
    descriptionKey: 'modelGpt1Desc',
  },
  // Era 2: Scaling Laws (2019-2020)
  {
    id: 'gpt2',
    name: 'GPT-2',
    params: '1.5B',
    maxUsers: 5_000,
    tokensPerTask: 800,
    marketPricePerMillion: 3.00,
    researchCost: 150,
    trainComputeCost: 25,
    maxQuality: 82,
    userGrowthMultiplier: 100,
    descriptionKey: 'modelGpt2Desc',
  },
  {
    id: 'gpt3',
    name: 'GPT-3',
    params: '175B',
    maxUsers: 50_000,
    tokensPerTask: 2_000,
    marketPricePerMillion: 2.00,
    researchCost: 400,
    trainComputeCost: 50,
    maxQuality: 85,
    userGrowthMultiplier: 500,
    descriptionKey: 'modelGpt3Desc',
  },
  // Era 3: ChatGPT Moment (2022-2023)
  {
    id: 'gpt35',
    name: 'GPT-3.5',
    params: '175B+',
    maxUsers: 500_000,
    tokensPerTask: 4_000,
    marketPricePerMillion: 1.50,
    researchCost: 1_000,
    trainComputeCost: 200,
    maxQuality: 88,
    userGrowthMultiplier: 2_000,
    descriptionKey: 'modelGpt35Desc',
  },
  {
    id: 'gpt4',
    name: 'GPT-4',
    params: '1T+',
    maxUsers: 5_000_000,
    tokensPerTask: 8_000,
    marketPricePerMillion: 1.00,
    researchCost: 2_500,
    trainComputeCost: 1_000,
    maxQuality: 90,
    userGrowthMultiplier: 10_000,
    descriptionKey: 'modelGpt4Desc',
  },
  {
    id: 'gpt41',
    name: 'GPT-4.1',
    params: '2T+',
    maxUsers: 20_000_000,
    tokensPerTask: 16_000,
    marketPricePerMillion: 0.80,
    researchCost: 6_000,
    trainComputeCost: 1_000,
    maxQuality: 92,
    userGrowthMultiplier: 40_000,
    descriptionKey: 'modelGpt41Desc',
  },
  // Era 4: New Players (2024-2025)
  {
    id: 'u1',
    name: 'U-1',
    params: '5T',
    maxUsers: 10_000_000,
    tokensPerTask: 32_000,
    marketPricePerMillion: 0.60,
    researchCost: 1_500,
    trainComputeCost: 1_200,
    maxQuality: 93,
    userGrowthMultiplier: 15_000,
    descriptionKey: 'modelU1Desc',
  },
  {
    id: 'u2',
    name: '2-U',
    params: '10T',
    maxUsers: 10_000_000,
    tokensPerTask: 64_000,
    marketPricePerMillion: 0.40,
    researchCost: 3_000,
    trainComputeCost: 2_000,
    maxQuality: 94,
    userGrowthMultiplier: 15_000,
    descriptionKey: 'model2UDesc',
  },

  // Era 5: Superintelligence Race (2026+)
  {
    id: 'gpt5',
    name: 'GPT-5',
    params: '50T',
    maxUsers: 500_000_000,
    tokensPerTask: 100_000,
    marketPricePerMillion: 0.20,
    researchCost: 15_000,
    trainComputeCost: 5_000,
    maxQuality: 96,
    userGrowthMultiplier: 500_000,
    descriptionKey: 'modelGpt5Desc',
  },
  {
    id: 'gpt6',
    name: 'GPT-6',
    params: '200T',
    maxUsers: 1_000_000_000,
    tokensPerTask: 150_000,
    marketPricePerMillion: 0.10,
    researchCost: 30_000,
    trainComputeCost: 10_000,
    maxQuality: 97,
    userGrowthMultiplier: 1_000_000,
    discontinuesOlderModels: true,
    descriptionKey: 'modelGpt6Desc',
  },
  {
    id: 'gpt7',
    name: 'GPT-7',
    params: '1000T',
    maxUsers: 2_000_000_000,
    tokensPerTask: 200_000,
    marketPricePerMillion: 0.05,
    researchCost: 50_000,
    trainComputeCost: 20_000,
    maxQuality: 98,
    userGrowthMultiplier: 2_000_000,
    descriptionKey: 'modelGpt7Desc',
  },
  // Era 6: AGI (2028+)
  {
    id: 'agi1',
    name: 'AGI-1',
    params: 'Adaptive',
    maxUsers: 3_000_000_000,
    tokensPerTask: 300_000,
    marketPricePerMillion: 0.02,
    researchCost: 100_000,
    trainComputeCost: 100_000,
    maxQuality: 99,
    userGrowthMultiplier: 3_000_000,
    descriptionKey: 'modelAgi1Desc',
  },
  {
    id: 'agi2',
    name: 'AGI-2',
    params: 'Self-evolving',
    maxUsers: 4_000_000_000,
    tokensPerTask: 500_000,
    marketPricePerMillion: 0.01,
    researchCost: 200_000,
    trainComputeCost: 200_000,
    maxQuality: 99.5,
    userGrowthMultiplier: 4_000_000,
    descriptionKey: 'modelAgi2Desc',
  },
  // Era 7: ASI (203X)
  {
    id: 'agi3',
    name: 'AGI-3',
    params: '∞',
    maxUsers: 5_000_000_000,
    tokensPerTask: 1_000_000,
    marketPricePerMillion: 0.001,
    researchCost: 500_000,
    trainComputeCost: 500_000,
    maxQuality: 99.9,
    userGrowthMultiplier: 5_000_000,
    descriptionKey: 'modelAgi3Desc',
  },
  {
    id: 'asi',
    name: 'ASI',
    params: '∞∞',
    maxUsers: 8_000_000_000,
    tokensPerTask: 2_000_000,
    marketPricePerMillion: 0.0001,
    researchCost: 1_000_000,
    trainComputeCost: 1_000_000,
    maxQuality: 100,
    userGrowthMultiplier: 8_000_000,
    triggersVictory: true,
    descriptionKey: 'modelAsiDesc',
  },
]

// Debug presets for testing different game stages
export interface DebugEra {
  id: string
  name: string
  money: number
  energy: number
  maxEnergy: number
  compute: number
  maxCompute: number
  research: number
  unlockedModels: string[]
  ownedHardware: string[]
}

export const DEBUG_ERAS: DebugEra[] = [
  {
    id: 'start',
    name: '1. Fresh Start',
    money: 10,
    energy: 50,
    maxEnergy: 100,
    compute: 20,
    maxCompute: 50,
    research: 0,
    unlockedModels: ['attention'],
    ownedHardware: ['laptop'],
  },
  {
    id: 'era1',
    name: '2. GPT-1 Era',
    money: 500,
    energy: 200,
    maxEnergy: 300,
    compute: 100,
    maxCompute: 200,
    research: 50,
    unlockedModels: ['attention', 'gpt1'],
    ownedHardware: ['laptop', 'used-gpu', 'gaming-gpu'],
  },
  {
    id: 'era2',
    name: '3. GPT-2/3 Era',
    money: 10_000,
    energy: 500,
    maxEnergy: 1000,
    compute: 500,
    maxCompute: 1000,
    research: 500,
    unlockedModels: ['attention', 'gpt1', 'gpt2', 'gpt3'],
    ownedHardware: ['laptop', 'used-gpu', 'gaming-gpu', 'pro-gpu', 'workstation'],
  },
  {
    id: 'era3',
    name: '4. ChatGPT Era (GPT-3.5/4)',
    money: 500_000,
    energy: 2000,
    maxEnergy: 5000,
    compute: 2000,
    maxCompute: 5000,
    research: 2_000,
    unlockedModels: ['attention', 'gpt1', 'gpt2', 'gpt3', 'gpt35', 'gpt4'],
    ownedHardware: ['laptop', 'used-gpu', 'gaming-gpu', 'pro-gpu', 'workstation', 'server-rack', 'gpu-cluster'],
  },
  {
    id: 'era4',
    name: '5. Datacenter Era (GPT-4.1/U)',
    money: 5_000_000,
    energy: 10000,
    maxEnergy: 50000,
    compute: 10000,
    maxCompute: 50000,
    research: 5_000,
    unlockedModels: ['attention', 'gpt1', 'gpt2', 'gpt3', 'gpt35', 'gpt4', 'gpt41', 'u1', 'u2'],
    ownedHardware: ['laptop', 'used-gpu', 'gaming-gpu', 'pro-gpu', 'workstation', 'server-rack', 'gpu-cluster', 'mini-datacenter', 'datacenter'],
  },
  {
    id: 'era5',
    name: '6. Superintelligence Era (GPT-5/6/7)',
    money: 50_000_000,
    energy: 100000,
    maxEnergy: 500000,
    compute: 100000,
    maxCompute: 500000,
    research: 100_000,
    unlockedModels: ['attention', 'gpt1', 'gpt2', 'gpt3', 'gpt35', 'gpt4', 'gpt41', 'u1', 'u2', 'gpt5', 'gpt6', 'gpt7'],
    ownedHardware: ['laptop', 'used-gpu', 'gaming-gpu', 'pro-gpu', 'workstation', 'server-rack', 'gpu-cluster', 'mini-datacenter', 'datacenter', 'mega-datacenter', 'datacenter-network', 'supercomputer'],
  },
  {
    id: 'era6',
    name: '7. AGI Era',
    money: 500_000_000,
    energy: 1000000,
    maxEnergy: 5000000,
    compute: 1000000,
    maxCompute: 5000000,
    research: 500_000,
    unlockedModels: ['attention', 'gpt1', 'gpt2', 'gpt3', 'gpt35', 'gpt4', 'gpt41', 'u1', 'u2', 'gpt5', 'gpt6', 'gpt7', 'agi1', 'agi2'],
    ownedHardware: ['laptop', 'used-gpu', 'gaming-gpu', 'pro-gpu', 'workstation', 'server-rack', 'gpu-cluster', 'mini-datacenter', 'datacenter', 'mega-datacenter', 'datacenter-network', 'supercomputer', 'satellite', 'quantum'],
  },
  {
    id: 'era7',
    name: '8. ASI Era (Endgame)',
    money: 5_000_000_000,
    energy: 10000000,
    maxEnergy: 50000000,
    compute: 10000000,
    maxCompute: 50000000,
    research: 2_000_000,
    unlockedModels: ['attention', 'gpt1', 'gpt2', 'gpt3', 'gpt35', 'gpt4', 'gpt41', 'u1', 'u2', 'gpt5', 'gpt6', 'gpt7', 'agi1', 'agi2', 'agi3', 'asi'],
    ownedHardware: ['laptop', 'used-gpu', 'gaming-gpu', 'pro-gpu', 'workstation', 'server-rack', 'gpu-cluster', 'mini-datacenter', 'datacenter', 'mega-datacenter', 'datacenter-network', 'supercomputer', 'satellite', 'quantum', 'dyson-compute', 'matrioshka'],
  },
]


// Upgrade tiers for organizing the upgrade list
export interface UpgradeTier {
  id: string
  name: string
  description: string
  requiredModel?: string // Tier unlocks when this model is unlocked
}

export const UPGRADE_TIERS: UpgradeTier[] = [
  { id: 'tier1', name: 'Garage', description: 'The beginning', requiredModel: undefined },
  { id: 'tier2', name: 'Startup', description: 'GPT-1 Era', requiredModel: 'gpt1' },
  { id: 'tier3', name: 'Scale-up', description: 'GPT-2/3 Era', requiredModel: 'gpt2' },
  { id: 'tier4', name: 'Enterprise', description: 'ChatGPT Era', requiredModel: 'gpt35' },
  { id: 'tier5', name: 'Hyperscale', description: 'GPT-4 Era', requiredModel: 'gpt4' },
  { id: 'tier6', name: 'Global', description: 'Competitor Era', requiredModel: 'u1' },
  { id: 'tier7', name: 'Superintelligence', description: 'GPT-5+ Era', requiredModel: 'gpt5' },
  { id: 'tier8', name: 'Singularity', description: 'AGI/ASI Era', requiredModel: 'agi1' },
]

// Helper to get tier from required model
export function getUpgradeTier(upgrade: UpgradeDef): string {
  const requiredModel = upgrade.requires?.model
  if (!requiredModel) return 'tier1'

  // Map model to tier
  const modelToTier: Record<string, string> = {
    'attention': 'tier1',
    'gpt1': 'tier2',
    'gpt2': 'tier3',
    'gpt3': 'tier3',
    'gpt35': 'tier4',
    'gpt4': 'tier5',
    'gpt41': 'tier5',
    'u1': 'tier6',
    'u2': 'tier6',
    'gpt5': 'tier7',
    'gpt6': 'tier7',
    'gpt7': 'tier7',
    'agi1': 'tier8',
    'agi2': 'tier8',
    'agi3': 'tier8',
    'asi': 'tier8',
  }

  return modelToTier[requiredModel] || 'tier1'
}

// Upgrade definitions
export interface UpgradeDef {
  id: string
  name?: string // fallback if no nameKey
  nameKey?: string // i18n key for translated name
  description?: string // fallback if no descKey
  descKey?: string // i18n key for translated description
  baseCost: number
  costMultiplier?: number // defaults to BALANCE.UPGRADE_COST_MULTIPLIER if not set
  currency: 'money'
  effect: {
    type: 'max_energy' | 'energy_regen' | 'max_compute' | 'hw_upgrade' | 'train_efficiency' | 'research_efficiency' | 'hw_boost' | 'auto_inference' | 'auto_train' | 'auto_research'
    value: number
    hardwareId?: string // for hw_upgrade type
  }
  maxOwned: number // -1 for unlimited
  requires?: {
    upgrade?: string
    model?: string
    money?: number
  }
}

export const UPGRADES: UpgradeDef[] = [
  // === EARLY GAME BOOST ===
  {
    id: 'laptop-overclock',
    name: 'Overclock Laptop',
    description: 'Push your laptop harder: +2 compute/sec',
    baseCost: 15,
    currency: 'money',
    effect: { type: 'hw_boost', value: 2 },
    maxOwned: 3,
  },

  // === ENERGY UPGRADES ===
  {
    id: 'better-outlet',
    name: 'Better Power Outlet',
    description: '+50 max energy',
    baseCost: 20,
    currency: 'money',
    effect: { type: 'max_energy', value: 50 },
    maxOwned: -1,
  },
  {
    id: 'solar-panel',
    name: 'Solar Panel',
    description: '+1 energy/sec regen',
    baseCost: 50,
    currency: 'money',
    effect: { type: 'energy_regen', value: 1 },
    maxOwned: -1,
  },
  {
    id: 'power-grid',
    name: 'Power Grid Connection',
    description: '+5 energy/sec regen',
    baseCost: 500,
    currency: 'money',
    effect: { type: 'energy_regen', value: 5 },
    maxOwned: -1,
    requires: { model: 'gpt1' },
  },
  {
    id: 'nuclear-plant',
    name: 'Nuclear Plant',
    description: '+20 energy/sec regen',
    baseCost: 5_000,
    currency: 'money',
    effect: { type: 'energy_regen', value: 20 },
    maxOwned: -1,
    requires: { model: 'gpt2' },
  },
  {
    id: 'fusion-reactor',
    name: 'Fusion Reactor',
    description: '+100 energy/sec regen',
    baseCost: 100_000,
    currency: 'money',
    effect: { type: 'energy_regen', value: 100 },
    maxOwned: -1,
    requires: { model: 'gpt35' },
  },
  {
    id: 'dyson-sphere',
    name: 'Dyson Sphere',
    description: '+1000 energy/sec regen',
    baseCost: 1_000_000_000, // 1B (was 10M)
    currency: 'money',
    effect: { type: 'energy_regen', value: 1000 },
    maxOwned: -1,
    requires: { model: 'gpt5' },
  },
  {
    id: 'stellar-tap',
    name: 'Stellar Energy Tap',
    description: '+5000 energy/sec regen',
    baseCost: 50_000_000_000, // 50B
    currency: 'money',
    effect: { type: 'energy_regen', value: 5000 },
    maxOwned: -1,
    requires: { model: 'gpt6' },
  },
  {
    id: 'galactic-grid',
    name: 'Galactic Power Grid',
    description: '+25000 energy/sec regen',
    baseCost: 500_000_000_000, // 500B
    currency: 'money',
    effect: { type: 'energy_regen', value: 25000 },
    maxOwned: -1,
    requires: { model: 'gpt7' },
  },
  {
    id: 'antimatter-reactor',
    name: 'Antimatter Reactor',
    description: '+100000 energy/sec regen',
    baseCost: 10_000_000_000_000, // 10T (was 1B)
    currency: 'money',
    effect: { type: 'energy_regen', value: 100000 },
    maxOwned: -1,
    requires: { model: 'agi1' },
  },

  // === HARDWARE UPGRADES ===
  // Tier 1: Garage (Attention)
  {
    id: 'hw-used-gpu',
    name: 'Varicia GTX 1060',
    description: 'Used GPU: +3 compute/sec',
    baseCost: 100,
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'used-gpu' },
    maxOwned: 1,
  },
  {
    id: 'hw-gaming-gpu',
    name: 'Varicia RTX 3080',
    description: 'Gaming GPU: +8 compute/sec',
    baseCost: 500,
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'gaming-gpu' },
    maxOwned: 1,
  },
  {
    id: 'hw-pro-gpu',
    name: 'Varicia RTX 4090',
    description: 'Pro GPU: +20 compute/sec',
    baseCost: 2_000,
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'pro-gpu' },
    maxOwned: 1,
  },

  // Tier 2: Startup (GPT-1)
  {
    id: 'hw-workstation',
    name: 'Titan Workstation Pro',
    description: 'Workstation: +50 compute/sec',
    baseCost: 10_000,
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'workstation' },
    maxOwned: 1,
    requires: { model: 'gpt1' },
  },
  {
    id: 'hw-server-rack',
    name: 'Nexus A100 Rack',
    description: 'Server rack (4x A100): +120 compute/sec',
    baseCost: 50_000,
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'server-rack' },
    maxOwned: 1,
    requires: { model: 'gpt2' },
  },

  // Tier 3: Scale-up (GPT-3)
  {
    id: 'hw-gpu-cluster',
    name: 'Nexus H100 Cluster',
    description: 'GPU cluster (8x H100): +300 compute/sec',
    baseCost: 200_000,
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'gpu-cluster' },
    maxOwned: 1,
    requires: { model: 'gpt3' },
  },
  {
    id: 'hw-mini-datacenter',
    name: 'OmniCloud Edge Node',
    description: 'Edge datacenter: +800 compute/sec',
    baseCost: 500_000,
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'mini-datacenter' },
    maxOwned: 1,
    requires: { model: 'gpt35' },
  },

  // Tier 4: Datacenter Era (GPT-4)
  {
    id: 'hw-datacenter',
    name: 'OmniCloud DC-1',
    description: 'Datacenter: +2K compute/sec',
    baseCost: 2_000_000,
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'datacenter' },
    maxOwned: 1,
    requires: { model: 'gpt4' },
  },
  {
    id: 'hw-mega-datacenter',
    name: 'OmniCloud HyperScale',
    description: 'Mega datacenter: +5K compute/sec',
    baseCost: 10_000_000,
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'mega-datacenter' },
    maxOwned: 1,
    requires: { model: 'gpt41' },
  },

  // Tier 5: Global Infrastructure (U-1, 2-U)
  {
    id: 'hw-datacenter-network',
    name: 'OmniCloud Global Network',
    description: 'Global network: +15K compute/sec',
    baseCost: 50_000_000,
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'datacenter-network' },
    maxOwned: 1,
    requires: { model: 'u1' },
  },
  {
    id: 'hw-supercomputer',
    name: 'Frontier X1 Supercomputer',
    description: 'Supercomputer: +50K compute/sec',
    baseCost: 250_000_000,
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'supercomputer' },
    maxOwned: 1,
    requires: { model: 'u2' },
  },

  // Tier 6: Next-Gen (GPT-5, GPT-6, GPT-7)
  {
    id: 'hw-satellite',
    name: 'SkyNet Moonlink Constellation',
    description: 'Orbital satellites: +200K compute/sec',
    baseCost: 50_000_000_000, // 50B (was 5B)
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'satellite' },
    maxOwned: 1,
    requires: { model: 'gpt5' },
  },
  {
    id: 'hw-quantum',
    name: 'QuantumCore QC-1024',
    description: 'Quantum computer: +1M compute/sec',
    baseCost: 500_000_000_000, // 500B (was 50B)
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'quantum' },
    maxOwned: 1,
    requires: { model: 'gpt6' },
  },

  // Tier 7: Post-Singularity (AGI, ASI)
  {
    id: 'hw-dyson-compute',
    name: 'Dyson Compute Sphere',
    description: 'Stellar-scale computing: +10M compute/sec',
    baseCost: 50_000_000_000_000, // 50T (was 1T)
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'dyson-compute' },
    maxOwned: 1,
    requires: { model: 'agi1' },
  },
  {
    id: 'hw-matrioshka',
    name: 'Matrioshka Brain',
    description: 'Galaxy-brain computing: +100M compute/sec',
    baseCost: 10_000_000_000_000_000, // 10Q (was 100T)
    currency: 'money',
    effect: { type: 'hw_upgrade', value: 0, hardwareId: 'matrioshka' },
    maxOwned: 1,
    requires: { model: 'agi3' },
  },

  // === COMPUTE CAPACITY ===
  {
    id: 'ram-upgrade',
    name: 'RAM Upgrade',
    description: '+50 max compute',
    baseCost: 50,
    currency: 'money',
    effect: { type: 'max_compute', value: 50 },
    maxOwned: -1,
  },
  {
    id: 'ssd-array',
    name: 'SSD Array',
    description: '+200 max compute',
    baseCost: 300,
    currency: 'money',
    effect: { type: 'max_compute', value: 200 },
    maxOwned: -1,
  },
  {
    id: 'memory-pool',
    name: 'Distributed Memory Pool',
    description: '+2K max compute',
    baseCost: 50_000,
    currency: 'money',
    effect: { type: 'max_compute', value: 2_000 },
    maxOwned: -1,
    requires: { model: 'gpt3' },
  },
  {
    id: 'compute-reservoir',
    name: 'Compute Reservoir',
    description: '+10K max compute',
    baseCost: 500_000,
    currency: 'money',
    effect: { type: 'max_compute', value: 10_000 },
    maxOwned: -1,
    requires: { model: 'gpt4' },
  },
  {
    id: 'neural-cache',
    name: 'Neural State Cache',
    description: '+50K max compute',
    baseCost: 5_000_000,
    currency: 'money',
    effect: { type: 'max_compute', value: 50_000 },
    maxOwned: -1,
    requires: { model: 'u1' },
  },
  {
    id: 'hyperscale-buffer',
    name: 'Hyperscale Compute Buffer',
    description: '+200K max compute',
    baseCost: 50_000_000,
    currency: 'money',
    effect: { type: 'max_compute', value: 200_000 },
    maxOwned: -1,
    requires: { model: 'gpt5' },
  },
  {
    id: 'planetary-compute',
    name: 'Planetary Compute Network',
    description: '+1M max compute',
    baseCost: 500_000_000,
    currency: 'money',
    effect: { type: 'max_compute', value: 1_000_000 },
    maxOwned: -1,
    requires: { model: 'gpt6' },
  },
  {
    id: 'stellar-compute',
    name: 'Stellar Compute Array',
    description: '+5M max compute',
    baseCost: 10_000_000_000,
    currency: 'money',
    effect: { type: 'max_compute', value: 5_000_000 },
    maxOwned: -1,
    requires: { model: 'agi1' },
  },
  {
    id: 'cosmic-compute',
    name: 'Cosmic Compute Substrate',
    description: '+25M max compute',
    baseCost: 500_000_000_000,
    currency: 'money',
    effect: { type: 'max_compute', value: 25_000_000 },
    maxOwned: -1,
    requires: { model: 'agi3' },
  },

  // === EFFICIENCY UPGRADES ===
  {
    id: 'better-optimizer',
    name: 'Better Optimizer',
    description: '+20% training efficiency',
    baseCost: 200,
    currency: 'money',
    effect: { type: 'train_efficiency', value: 0.2 },
    maxOwned: 5,
  },

  // === RESEARCH UPGRADES ===
  {
    id: 'research-papers',
    name: 'Research Papers',
    description: '+20% research efficiency',
    baseCost: 300,
    currency: 'money',
    effect: { type: 'research_efficiency', value: 0.2 },
    maxOwned: 5,
  },
  {
    id: 'research-intern',
    name: 'Research Intern',
    description: 'Hire an intern: +50% research efficiency',
    baseCost: 1_000,
    currency: 'money',
    effect: { type: 'research_efficiency', value: 0.5 },
    maxOwned: 3,
    requires: { model: 'gpt1' },
  },
  {
    id: 'research-team',
    name: 'Research Team',
    description: 'Build a team: +100% research efficiency',
    baseCost: 10_000,
    currency: 'money',
    effect: { type: 'research_efficiency', value: 1.0 },
    maxOwned: 3,
    requires: { model: 'gpt2' },
  },
  {
    id: 'phd-scientists',
    name: 'PhD Scientists',
    description: 'Hire top talent: +200% research efficiency',
    baseCost: 50_000,
    currency: 'money',
    effect: { type: 'research_efficiency', value: 2.0 },
    maxOwned: 5,
    requires: { model: 'gpt3' },
  },
  {
    id: 'research-lab',
    name: 'Research Lab',
    description: 'Dedicated facility: +500% research efficiency',
    baseCost: 200_000,
    currency: 'money',
    effect: { type: 'research_efficiency', value: 5.0 },
    maxOwned: 3,
    requires: { model: 'gpt35' },
  },
  {
    id: 'ai-research-assistant',
    name: 'AI Research Assistant',
    description: 'GPT-4 helps with research: +1000% research efficiency',
    baseCost: 1_000_000,
    currency: 'money',
    effect: { type: 'research_efficiency', value: 10.0 },
    maxOwned: 5,
    requires: { model: 'gpt4' },
  },
  {
    id: 'ai-scientist',
    name: 'AI Scientist',
    description: 'AI conducts experiments autonomously: +2000% research efficiency',
    baseCost: 10_000_000,
    currency: 'money',
    effect: { type: 'research_efficiency', value: 20.0 },
    maxOwned: 5,
    requires: { model: 'gpt41' },
  },
  {
    id: 'research-swarm',
    name: 'AI Research Swarm',
    description: 'Thousands of AI researchers: +5000% research efficiency',
    baseCost: 100_000_000,
    currency: 'money',
    effect: { type: 'research_efficiency', value: 50.0 },
    maxOwned: 5,
    requires: { model: 'u1' },
  },
  {
    id: 'recursive-research',
    name: 'Recursive Self-Improvement',
    description: 'AI improves AI: +10000% research efficiency',
    baseCost: 1_000_000_000,
    currency: 'money',
    effect: { type: 'research_efficiency', value: 100.0 },
    maxOwned: 10,
    requires: { model: 'gpt5' },
  },
  {
    id: 'singularity-research',
    name: 'Singularity Research',
    description: 'Beyond comprehension: +50000% research efficiency',
    baseCost: 100_000_000_000,
    currency: 'money',
    effect: { type: 'research_efficiency', value: 500.0 },
    maxOwned: -1,
    requires: { model: 'agi1' },
  },

  // === AUTO-INFERENCE (Self-Service) ===
  {
    id: 'basic-chatbot',
    name: 'Basic Chatbot',
    description: 'A simple web widget: +0.2 auto-inferences/sec',
    baseCost: 50,
    currency: 'money',
    effect: { type: 'auto_inference', value: 0.2 },
    maxOwned: 1,
  },
  {
    id: 'self-service-portal',
    name: 'Self-Service Portal',
    description: 'Users serve themselves: +0.5 auto-inferences/sec',
    baseCost: 500,
    currency: 'money',
    effect: { type: 'auto_inference', value: 0.5 },
    maxOwned: 1,
    requires: { model: 'gpt1' },
  },
  {
    id: 'api-access',
    name: 'API Access',
    description: 'Developers integrate your model: +1 auto-inference/sec',
    baseCost: 2_000,
    currency: 'money',
    effect: { type: 'auto_inference', value: 1 },
    maxOwned: 1,
    requires: { model: 'gpt1' },
  },
  {
    id: 'mobile-app',
    name: 'Mobile App',
    description: 'Users on the go: +2 auto-inferences/sec',
    baseCost: 10_000,
    currency: 'money',
    effect: { type: 'auto_inference', value: 2 },
    maxOwned: 1,
    requires: { model: 'gpt2' },
  },
  {
    id: 'bulk-api',
    name: 'Bulk API',
    description: 'High-volume batch processing: +3 auto-inferences/sec',
    baseCost: 30_000,
    currency: 'money',
    effect: { type: 'auto_inference', value: 3 },
    maxOwned: 1,
    requires: { model: 'gpt3' },
  },
  {
    id: 'enterprise-tier',
    name: 'Enterprise Tier',
    description: 'Big clients, big volume: +5 auto-inferences/sec',
    baseCost: 100_000,
    currency: 'money',
    effect: { type: 'auto_inference', value: 5 },
    maxOwned: 1,
    requires: { model: 'gpt35' },
  },
  {
    id: 'global-cdn',
    name: 'Global CDN',
    description: 'Worldwide low-latency: +10 auto-inferences/sec',
    baseCost: 500_000,
    currency: 'money',
    effect: { type: 'auto_inference', value: 10 },
    maxOwned: 1,
    requires: { model: 'gpt4' },
  },
  {
    id: 'neural-edge',
    name: 'Neural Edge Network',
    description: 'AI at the edge: +20 auto-inferences/sec',
    baseCost: 5_000_000,
    currency: 'money',
    effect: { type: 'auto_inference', value: 20 },
    maxOwned: 1,
    requires: { model: 'u1' },
  },
  {
    id: 'quantum-inference',
    name: 'Quantum Inference',
    description: 'Quantum-accelerated inference: +50 auto-inferences/sec',
    baseCost: 50_000_000,
    currency: 'money',
    effect: { type: 'auto_inference', value: 50 },
    maxOwned: 1,
    requires: { model: 'gpt5' },
  },
  {
    id: 'omnipresent-ai',
    name: 'Omnipresent AI',
    description: 'AI everywhere, all at once: +100 auto-inferences/sec',
    baseCost: 500_000_000,
    currency: 'money',
    effect: { type: 'auto_inference', value: 100 },
    maxOwned: 1,
    requires: { model: 'agi1' },
  },

  // === AUTO-TRAIN ===
  {
    id: 'auto-train-1',
    name: 'Training Scheduler',
    description: 'Automated training runs: +0.1 auto-trains/sec',
    baseCost: 5_000,
    currency: 'money',
    effect: { type: 'auto_train', value: 0.1 },
    maxOwned: 1,
    requires: { model: 'gpt2' },
  },
  {
    id: 'auto-train-2',
    name: 'Training Pipeline',
    description: 'Continuous training: +0.2 auto-trains/sec',
    baseCost: 50_000,
    currency: 'money',
    effect: { type: 'auto_train', value: 0.2 },
    maxOwned: 1,
    requires: { model: 'gpt3' },
  },
  {
    id: 'auto-train-3',
    name: 'MLOps Platform',
    description: 'Full automation: +0.5 auto-trains/sec',
    baseCost: 500_000,
    currency: 'money',
    effect: { type: 'auto_train', value: 0.5 },
    maxOwned: 1,
    requires: { model: 'gpt4' },
  },
  {
    id: 'auto-train-4',
    name: 'Neural Architecture Search',
    description: 'AI trains AI: +1 auto-train/sec',
    baseCost: 5_000_000,
    currency: 'money',
    effect: { type: 'auto_train', value: 1 },
    maxOwned: 1,
    requires: { model: 'gpt5' },
  },
  {
    id: 'auto-train-5',
    name: 'Self-Improving Training',
    description: 'Recursive improvement: +2 auto-trains/sec',
    baseCost: 50_000_000,
    currency: 'money',
    effect: { type: 'auto_train', value: 2 },
    maxOwned: 1,
    requires: { model: 'agi1' },
  },

  // === AUTO-RESEARCH ===
  {
    id: 'auto-research-1',
    name: 'Research Intern',
    description: 'Entry-level help: +0.1 auto-research/sec',
    baseCost: 10_000,
    currency: 'money',
    effect: { type: 'auto_research', value: 0.1 },
    maxOwned: 1,
    requires: { model: 'gpt2' },
  },
  {
    id: 'auto-research-2',
    name: 'Research Team',
    description: 'Dedicated researchers: +0.2 auto-research/sec',
    baseCost: 100_000,
    currency: 'money',
    effect: { type: 'auto_research', value: 0.2 },
    maxOwned: 1,
    requires: { model: 'gpt3' },
  },
  {
    id: 'auto-research-3',
    name: 'Research Division',
    description: 'Full department: +0.5 auto-research/sec',
    baseCost: 1_000_000,
    currency: 'money',
    effect: { type: 'auto_research', value: 0.5 },
    maxOwned: 1,
    requires: { model: 'gpt4' },
  },
  {
    id: 'auto-research-4',
    name: 'AI Research Lab',
    description: 'AI-assisted discovery: +1 auto-research/sec',
    baseCost: 10_000_000,
    currency: 'money',
    effect: { type: 'auto_research', value: 1 },
    maxOwned: 1,
    requires: { model: 'gpt5' },
  },
  {
    id: 'auto-research-5',
    name: 'Autonomous Research',
    description: 'AI does the research: +2 auto-research/sec',
    baseCost: 100_000_000,
    currency: 'money',
    effect: { type: 'auto_research', value: 2 },
    maxOwned: 1,
    requires: { model: 'agi1' },
  },
]
